import {
  Card,
  Image,
  Spin,
  message as antdMessage,
  Upload,
  Button
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  CheckCircleFilled
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../contexts/AuthContext';
import { compressFile } from '../../../utils/filesManagement/compressFile';
import { fileToBase64 } from '../../../utils/filesManagement/fileToBase64';
import { fileExtensionManage } from '../../../utils/filesManagement/fileExtensionManage';

/**
 * Component for displaying a gallery of announcement photos.
 *
 * @component
 * @param {Object} props - The props that define this component.
 * @param {Array} props.photos - Array of photo objects.
 * @param {Array} props.selectedAnouncePhotos - Array of selected photo objects.
 * @param {Function} props.setSelectedAnouncePhotos - Function to set selected photos.
 * @returns {JSX.Element}
 */

export const AnouncePhotoGallery = ({
  photos,
  setPhotos,
  selectedAnouncePhotos,
  setSelectedAnouncePhotos
}) => {
  const { t } = useTranslation();
  const { token } = useAuthContext();

  const handleCardClick = (photo) => {
    if (
      !selectedAnouncePhotos.some((selected) => selected === photo) &&
      selectedAnouncePhotos.length >= 10
    ) {
      return antdMessage.warning(t('supervision.messages.anounce_photo_limit'));
    }

    setSelectedAnouncePhotos((prevSelected) => {
      const isAlreadySelected = prevSelected.some(
        (selected) => selected === photo
      );
      if (isAlreadySelected) {
        return prevSelected.filter((selected) => selected !== photo);
      }
      return [...prevSelected, photo];
    });

    return null;
  };

  const handleDelete = (e, photo) => {
    e.stopPropagation();
    setPhotos((prev) => prev.filter((p) => p !== photo));
    setSelectedAnouncePhotos((prev) =>
      prev.filter((selected) => selected !== photo)
    );
  };

  const handleAddPhoto = async (file) => {
    try {
      const compressed = await compressFile(file);

      if (!compressed) {
        antdMessage.error('Compression failed');
        return false;
      }

      const extension = compressed.name.split('.').pop().toLowerCase();

      if (!fileExtensionManage(extension)) {
        antdMessage.error('Only JPG or PNG allowed');
        return false;
      }

      const base64 = await fileToBase64(compressed);

      const newPhoto = {
        uid: `${Date.now()}`,
        name: compressed.name,
        status: 'done',
        url: base64,
        file: compressed
      };

      setPhotos((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        return [...current, newPhoto];
      });
    } catch (err) {
      antdMessage.error('Error adding photo');
    }

    return false;
  };

  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}
    >
      {(photos || []).map((photo) => {
        const isSelected = selectedAnouncePhotos.some(
          (selected) => selected === photo
        );

        return (
          <Card
            key={photo?.uid || photo?.url || photo}
            hoverable
            style={{
              width: 240,
              margin: '10px',
              border: isSelected ? '3px solid orange' : '3px solid transparent',
              position: 'relative',
              transition: 'border 0.2s ease'
            }}
            onClick={() => handleCardClick(photo)}
            bodyStyle={{ padding: 0 }}
          >
            {/* Badge sélection */}
            {isSelected && (
              <CheckCircleFilled
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  fontSize: 22,
                  color: 'orange',
                  zIndex: 1,
                  background: 'white',
                  borderRadius: '50%'
                }}
              />
            )}

            {/* Bouton suppression */}
            <Button
              danger
              shape="circle"
              icon={<DeleteOutlined />}
              size="small"
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1
              }}
              onClick={(e) => handleDelete(e, photo)}
            />

            {photo?.url ? (
              <Image
                alt="anounce-photos"
                src={photo.url}
                style={{ width: '100%', display: 'block' }}
                placeholder={<Spin size="large" />}
                preview={false}
              />
            ) : (
              <Image
                alt="anounce-photos"
                src={`${process.env.REACT_APP_API_URL}/anounces/get-url/${photo}/${token}`}
                style={{ width: '100%', display: 'block' }}
                placeholder={<Spin size="large" />}
                preview={false}
              />
            )}
          </Card>
        );
      })}

      {/* Bouton ajout */}
      <Upload
        showUploadList={false}
        beforeUpload={handleAddPhoto}
        accept=".jpg,.jpeg,.png"
      >
        <Card
          hoverable
          style={{
            width: 240,
            height: 180,
            margin: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px dashed #d9d9d9',
            cursor: 'pointer'
          }}
          bodyStyle={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%'
          }}
        >
          <PlusOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
          <p style={{ color: '#bfbfbf', marginTop: 8 }}>
            {t('files.create.action')}
          </p>
        </Card>
      </Upload>
    </div>
  );
};

AnouncePhotoGallery.propTypes = {
  photos: PropTypes.arrayOf(PropTypes.shape({})),
  setPhotos: PropTypes.func.isRequired,
  selectedAnouncePhotos: PropTypes.arrayOf(PropTypes.shape({})),
  setSelectedAnouncePhotos: PropTypes.func.isRequired
};

AnouncePhotoGallery.defaultProps = {
  selectedAnouncePhotos: [],
  photos: []
};
